import { useState } from "react";
import { CheckCircle2, Undo2, XCircle, Lock, Copy, MessageSquareWarning } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../../lib/api";
import { Btn } from "../kit/Kit";
import { MotivoSelect } from "../crm/StageFlow";
import { useVersions, VersionPdf } from "./Historial";

const fd = (s) => (s ? new Date(s).toLocaleDateString("es-MX", { dateStyle: "long" }) : "");

export const ReviewPanel = ({ id, exp, setExp }) => {
  const versions = useVersions(id, exp.status + exp.version);
  const v = versions?.find((x) => x.version === exp.version);
  const [com, setCom] = useState("");
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (accion) => {
    if (accion !== "aprobar" && com.trim().length < 3) { toast.error("Escribe un comentario para devolver o rechazar"); return; }
    if (accion === "rechazar" && !motivo) { toast.error("Selecciona el motivo del rechazo"); return; }
    const q = { aprobar: "¿Aprobar el expediente? Quedará bloqueado.", devolver: "¿Devolver al analista para corrección?", rechazar: "¿Rechazar en definitiva? El expediente quedará como Rechazado." }[accion];
    if (!window.confirm(q)) return;
    setBusy(true);
    try {
      const r = await http.post(`/expedientes/${id}/decision`, { accion, comentario: com, ...(accion === "rechazar" ? { motivo } : {}) });
      setExp(r.data);
      toast.success({ aprobar: "Expediente aprobado", devolver: "Devuelto para corrección", rechazar: "Expediente rechazado" }[accion]);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="mt-6 rounded-2xl border-2 border-[#111] bg-white p-6 shadow-md sm:p-8" data-testid="review-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#E8643C]">Por revisar · v{exp.version}</p>
          <h3 className="font-serif-display mt-1 text-2xl font-bold">Resolución del Director de Crédito</h3>
          <p className="mt-1 text-sm text-[#5C5850]">Enviado por {v?.submitted_by?.name || exp.owner_name} {v && `el ${fd(v.submitted_at)}`}. Revisa el expediente en solo lectura y el PDF de la versión.</p>
        </div>
        {v && <VersionPdf id={id} v={v} nombre={exp.nombre} kind="primary" label={`Ver PDF v${v.version}`} />}
      </div>
      <textarea data-testid="review-comment" value={com} onChange={(e) => setCom(e.target.value)} rows={3}
        placeholder="Comentario (opcional para aprobar, obligatorio para devolver o rechazar)"
        className="mt-5 w-full rounded-xl border border-[#E2DDD5] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#E8643C] focus:bg-white" />
      <MotivoSelect value={motivo} onChange={setMotivo} />
      <div className="mt-4 flex flex-wrap gap-3">
        <Btn kind="accent" disabled={busy} onClick={() => act("aprobar")} data-testid="review-aprobar-button"><CheckCircle2 size={16} /> Aprobar</Btn>
        <Btn kind="secondary" disabled={busy} onClick={() => act("devolver")} data-testid="review-devolver-button"><Undo2 size={16} /> Devolver para corrección</Btn>
        <Btn kind="ghost" className="text-rose-700 hover:bg-rose-50" disabled={busy} onClick={() => act("rechazar")} data-testid="review-rechazar-button"><XCircle size={16} /> Rechazar definitivo</Btn>
      </div>
    </section>
  );
};

export const StatusBanner = ({ id, exp, setExp, isDirector }) => {
  const versions = useVersions(id, exp.status + exp.version);
  const rv = exp.review;
  const newVersion = async () => {
    if (!window.confirm(`¿Crear la versión v${exp.version + 1} en borrador a partir de v${exp.version}?`)) return;
    try { const r = await http.post(`/expedientes/${id}/new-version`); setExp(r.data); toast.success(`Versión v${r.data.version} creada en borrador`); } catch (e) { toast.error(errMsg(e)); }
  };
  if (exp.status === "borrador" && rv?.accion === "devolver") {
    return (
      <div className="mt-6 flex gap-4 rounded-2xl border border-orange-200 bg-orange-50 px-6 py-4" data-testid="devuelto-banner">
        <MessageSquareWarning className="mt-0.5 shrink-0 text-orange-600" size={20} />
        <div><p className="text-sm font-semibold text-orange-900">Devuelto para corrección por {rv.by?.name} · {fd(rv.at)} · ahora trabajas en v{exp.version}</p>
          <p className="mt-1 text-sm text-orange-800" data-testid="devuelto-comentario">“{rv.comentario}”</p></div>
      </div>
    );
  }
  if (exp.status === "en_revision" && !isDirector) {
    return <p className="mt-6 flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-800" data-testid="readonly-banner"><Lock size={15} /> v{exp.version} en revisión del Director de Crédito: solo lectura.</p>;
  }
  if (exp.status === "aprobado" || exp.status === "rechazado") {
    const ok = exp.status === "aprobado";
    const v = versions?.find((x) => x.version === exp.version);
    return (
      <div className={`mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border px-6 py-4 ${ok ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`} data-testid={ok ? "aprobado-banner" : "rechazado-banner"}>
        <div className="flex gap-3">
          {ok ? <Lock className="mt-0.5 text-emerald-700" size={18} /> : <XCircle className="mt-0.5 text-rose-700" size={18} />}
          <div>
            <p className={`text-sm font-semibold ${ok ? "text-emerald-900" : "text-rose-900"}`}>{ok ? "Aprobado" : "Rechazado"} por {rv?.by?.name} · {fd(rv?.at)} — v{exp.version} bloqueada</p>
            {rv?.comentario && <p className={`mt-1 text-sm ${ok ? "text-emerald-800" : "text-rose-800"}`}>“{rv.comentario}”</p>}
          </div>
        </div>
        <div className="flex gap-2">
          {v && <VersionPdf id={id} v={v} nombre={exp.nombre} label={ok ? "PDF final firmado" : `PDF v${v.version}`} />}
          <Btn kind="primary" className="px-3.5 py-2" onClick={newVersion} data-testid="nueva-version-button"><Copy size={15} /> Crear nueva versión</Btn>
        </div>
      </div>
    );
  }
  return null;
};
